import { useEffect, useState } from 'react';
import { Search, Trash2, Upload, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { AdminSectionTabs } from '@/components/admin/AdminSectionTabs';
import { SubscribersSubNav } from '@/components/admin/SubscribersSubNav';
import { SubscribersTable } from '@/components/admin/SubscribersTable';
import { CsvImportDialog } from '@/components/admin/CsvImportDialog';
import { AddSubscriberDialog } from '@/components/admin/AddSubscriberDialog';
import { PaginationFooter } from '@/components/admin/PaginationFooter';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { adminSubscribersService } from '@/services/admin-subscribers.service';
import { adminSubscriberListsService } from '@/services/admin-subscriber-lists.service';
import { ApiError } from '@/services/http';
import type { SubscriberListView, SubscriberView } from '@/types/api';

const DEFAULT_PAGE_SIZE = 100;

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
  { value: 'bounced', label: 'Bounced' },
  { value: 'complained', label: 'Complained' },
];

export function AdminSubscribersPage() {
  const [subscribers, setSubscribers] = useState<SubscriberView[]>([]);
  const [lists, setLists] = useState<SubscriberListView[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [status, setStatus] = useState('');
  const [listFilter, setListFilter] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const [importOpen, setImportOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<SubscriberView | null>(null);

  // Bulk selection (current page).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    adminSubscriberListsService
      .list()
      .then(setLists)
      .catch(() => {
        /* non-fatal — the assign/filter selects just stay empty */
      });
  }, []);

  const reload = () => {
    setLoading(true);
    setSelectedIds(new Set());
    adminSubscribersService
      .list({
        page,
        pageSize,
        status: status || undefined,
        listId: listFilter || undefined,
        q: appliedSearch || undefined,
      })
      .then((res) => {
        setSubscribers(res.subscribers);
        setTotalCount(res.totalCount);
      })
      .catch((err) => toast.error(err instanceof ApiError ? err.message : 'Could not load subscribers.'))
      .finally(() => setLoading(false));
  };

  useEffect(reload, [status, listFilter, appliedSearch, page, pageSize]);

  const refreshLists = async () => {
    try {
      setLists(await adminSubscriberListsService.list());
    } catch {
      /* non-fatal */
    }
  };

  const toggleSelect = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(subscribers.map((s) => s.id)) : new Set());
  };

  const handleAssign = async (id: string, listId: string | null) => {
    setBusyId(id);
    try {
      const updated = await adminSubscribersService.assignList(id, listId);
      setSubscribers((prev) => prev.map((s) => (s.id === id ? updated : s)));
      toast.success(listId ? `Moved to ${updated.listName}.` : 'Removed from list.');
      void refreshLists();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update list.');
    } finally {
      setBusyId(null);
    }
  };

  const handleReactivate = async (id: string) => {
    setBusyId(id);
    try {
      const updated = await adminSubscribersService.reactivate(id);
      setSubscribers((prev) => prev.map((s) => (s.id === id ? updated : s)));
      toast.success('Subscriber reactivated.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not reactivate.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    setBusyId(target.id);
    try {
      await adminSubscribersService.remove(target.id);
      setSubscribers((prev) => prev.filter((s) => s.id !== target.id));
      setTotalCount((c) => Math.max(0, c - 1));
      toast.success('Subscriber deleted.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete.');
    } finally {
      setBusyId(null);
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    setBulkConfirmOpen(false);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      const { deleted } = await adminSubscribersService.bulkDelete(ids);
      toast.success(`Deleted ${deleted} subscriber${deleted === 1 ? '' : 's'}.`);
      setSelectedIds(new Set());
      reload();
      void refreshLists();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Bulk delete failed.');
    } finally {
      setBulkDeleting(false);
    }
  };

  return (
    <AppShell mode="parent">
      <div className="flex flex-col gap-6">
        <AdminSectionTabs />
        <SubscribersSubNav />

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="or-kicker text-or-orange-600">Admin · Subscribers</div>
            <h1 className="mt-1 font-display text-[22px] font-bold text-or-slate-900 sm:text-[26px]">
              Subscribers
            </h1>
            <p className="mt-1 max-w-xl text-[13px] text-or-slate-600">
              Your standalone marketing audience. Add or import subscribers, assign them to a list,
              reactivate, or remove them. New account signups are auto-synced here.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="primary" size="md" onClick={() => setAddOpen(true)}>
              <UserPlus className="h-4 w-4" aria-hidden />
              Add subscriber
            </Button>
            <Button variant="secondary" size="md" onClick={() => setImportOpen(true)}>
              <Upload className="h-4 w-4" aria-hidden />
              Import CSV
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value || 'all'}
              type="button"
              onClick={() => {
                setStatus(f.value);
                setPage(1);
              }}
              aria-pressed={status === f.value}
              className={
                status === f.value
                  ? 'rounded-full bg-or-slate-900 px-3.5 py-1.5 text-[13px] font-semibold text-white'
                  : 'rounded-full border border-or-slate-200 bg-white px-3.5 py-1.5 text-[13px] font-semibold text-or-slate-700 hover:bg-or-slate-50'
              }
            >
              {f.label}
            </button>
          ))}

          <select
            value={listFilter}
            onChange={(e) => {
              setListFilter(e.target.value);
              setPage(1);
            }}
            className="ml-auto h-9 rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
          >
            <option value="">All lists</option>
            {lists.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name} ({l.memberCount})
              </option>
            ))}
          </select>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setAppliedSearch(search.trim());
            }}
            className="flex items-center gap-2"
          >
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-or-slate-400"
                aria-hidden
              />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search email or name"
                className="h-9 w-56 rounded-lg border border-or-slate-200 bg-white pl-8 pr-3 text-[13px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
              />
            </div>
            <Button variant="secondary" size="sm" type="submit">
              Search
            </Button>
          </form>
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 ? (
          <div className="flex items-center justify-between rounded-xl border border-or-slate-200 bg-or-slate-50 px-4 py-2.5">
            <span className="text-[13px] font-semibold text-or-slate-700">
              {selectedIds.size} selected
            </span>
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={() => setSelectedIds(new Set())}>
                Clear
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => setBulkConfirmOpen(true)}
                disabled={bulkDeleting}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
                Delete selected
              </Button>
            </div>
          </div>
        ) : null}

        <SubscribersTable
          subscribers={subscribers}
          lists={lists}
          busyId={busyId}
          loading={loading}
          onAssign={handleAssign}
          onReactivate={handleReactivate}
          onDelete={setPendingDelete}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          onToggleAll={toggleAll}
        />

        <PaginationFooter
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          noun="subscribers"
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </div>

      <AddSubscriberDialog
        open={addOpen}
        lists={lists}
        onClose={() => setAddOpen(false)}
        onCreated={() => {
          setPage(1);
          reload();
          void refreshLists();
        }}
      />

      <CsvImportDialog
        open={importOpen}
        lists={lists}
        onClose={() => setImportOpen(false)}
        onImported={(summary) => {
          toast.success(
            `Imported: ${summary.created} new, ${summary.updated} updated, ${summary.skipped} skipped` +
              (summary.invalid.length ? `, ${summary.invalid.length} invalid` : '') +
              '.',
          );
          setPage(1);
          reload();
          void refreshLists();
        }}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(next) => {
          if (!next) setPendingDelete(null);
        }}
        title="Delete this subscriber?"
        description={
          pendingDelete
            ? `This permanently removes ${pendingDelete.email} from your audience. This can't be undone.`
            : ''
        }
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={handleDelete}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        onOpenChange={(next) => {
          if (!next) setBulkConfirmOpen(false);
        }}
        title={`Delete ${selectedIds.size} subscriber${selectedIds.size === 1 ? '' : 's'}?`}
        description="This permanently removes the selected subscribers from your audience. This can't be undone."
        confirmLabel="Delete selected"
        confirmVariant="danger"
        onConfirm={handleBulkDelete}
      />
    </AppShell>
  );
}
